import React from 'react';
import { Box, Typography, TextField, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CustomAccordion from '../ui/CustomAccordion';

interface Comment {
  id: string;
  author: string;
  date: string;
  message: string;
}

interface CommentsSectionProps {
  comments?: Comment[];
  expanded: boolean;
  onExpandChange: () => void;
  newComment: string;
  onNewCommentChange: (comment: string) => void;
  onSendComment?: () => void;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  expanded,
  onExpandChange,
  newComment,
  onNewCommentChange,
  onSendComment,
}) => {
  return (
    <CustomAccordion
      title={`Comments (${comments?.length || 0})`}
      expanded={expanded}
      onChange={onExpandChange}
      sx={{
        mt: 1,
        boxShadow: 'none',
        border: '1px solid #E9E9E9',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': { minHeight: '40px', py: 0.5 },
        '& .MuiAccordionSummary-root.Mui-expanded': { minHeight: '40px', py: 0.5 },
        '& .MuiAccordionSummary-content': { margin: '0 !important' },
        '& .MuiAccordionSummary-content.Mui-expanded': { margin: '0 !important' },
      }}
      summarySx={{
        backgroundColor: '#F6F7FB',
        fontWeight: 600,
      }}
      detailsSx={{ p: 1, px: 1.5 }}
    >
      <Box
        sx={{
          maxHeight: '200px',
          overflowY: 'auto',
          mb: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        {comments && comments.length > 0 ? (
          comments.map((comment) => (
            <Box
              key={comment.id}
              sx={{
                backgroundColor: '#F6F7FB',
                borderRadius: '6px',
                padding: 0.75,
                borderLeft: '3px solid #255E5E',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#255E5E',
                  }}
                >
                  {comment.author}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '10px',
                    color: 'text.secondary',
                  }}
                >
                  {comment.date}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '12px',
                  color: '#333',
                }}
              >
                {comment.message}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No comments yet
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => onNewCommentChange(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={onSendComment}
          sx={{
            backgroundColor: '#255E5E',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1a4444',
            },
          }}
          disabled={!newComment.trim()}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </CustomAccordion>
  );
};

export default CommentsSection;
